import { useEffect, useState } from 'react';
import { useMalleTips } from '@/hooks/useMalleTips';
import styles from './TipRotator.module.css';

interface TipRotatorProps {
  slug: string;
  /** Rotations-Intervall in ms. Default 30 000 (30 s). */
  intervalMs?: number;
}

/**
 * Banner unter dem PlanPage-Header, der alle 30 s einen neuen Tipp aus
 * der gemergten Liste (Custom-Tipps + KI-Tipps) zeigt. Verschwindet,
 * wenn keine Tipps verfügbar (z.B. wenn Anthropic-Key fehlt und keine
 * eigenen gepflegt sind).
 */
export function TipRotator({ slug, intervalMs = 30_000 }: TipRotatorProps) {
  const { data, isLoading } = useMalleTips(slug);
  const tips = data?.tips ?? [];
  const [index, setIndex] = useState(0);

  // Beim Wechsel der Tipp-Liste auf Index 0 resetten (z.B. nach
  // Regenerate-Aktion vom Admin) — sonst zeigt der Banner ggf. den vorigen
  // Tipp einer alten Liste.
  useEffect(() => {
    setIndex(0);
  }, [data]);

  useEffect(() => {
    if (tips.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % tips.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [tips.length, intervalMs]);

  if (isLoading || tips.length === 0) return null;
  const current = tips[index];

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <span className={styles.badge}>KI-Tipp</span>
      <div key={index} className={styles.tip}>{current}</div>
    </div>
  );
}
